import { Component } from 'preact'
import { BoundedContext, getBoundedContextOfComponent } from '../boundedContexts'

type ImportFn = (subPath: string) => Promise<any>

const BOUNDED_CONTEXT_IMPORTS_MAP: Record<BoundedContext, ImportFn> = {
  'destination/modules/': async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'destination/modules/' + subPath + '.tsx'),
  'control/modules/': async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'control/modules/' + subPath + '.tsx'),
  'shared/modules/': async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'shared/modules/' + subPath + '.tsx')
}

export async function jsxLoader(componentPath: string): Promise<typeof Component> {
  const boundedContext = getBoundedContextOfComponent(componentPath)
  if (boundedContext !== undefined) {
    try {
      const subPath = getSubPath(componentPath, boundedContext)
      const file = await BOUNDED_CONTEXT_IMPORTS_MAP[boundedContext](subPath)
      return file.default
    } catch (error) {
      if (error.code !== 'MODULE_NOT_FOUND') {
        throw error
      }
    }
    throw new Error(`[import|Bounded Context] Component ${componentPath} not found`)
  }

  return await legacyJsxLoader(componentPath)
}

const getSubPath = (componentPath: string, boundedContext: BoundedContext): string => {
  /**
   * For some reasons, webpack requires import() function to have a literal string with a trailing slash.
   * This is why we cut the first chunk from the componentPath and write it manually
   */
  return componentPath.replace(boundedContext, '')
}

const LEGACY_IMPORTS_GUESSING_QUEUE: ImportFn[] = [
  async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'preact-components/' + subPath + '.tsx'),
  async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'preact-components/' + subPath + '.sync.tsx'),
  async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'preact-components/' + subPath + '.js'),
  async (subPath) => await import(/* webpackMode: 'eager', webpackExclude: /server/ */ 'preact-components/' + subPath + '.sync.js')
]

const legacyJsxLoader = async (componentName: string): Promise<typeof Component> => {
  for (const imp of LEGACY_IMPORTS_GUESSING_QUEUE) {
    try {
      return (await imp(componentName)).default
    } catch (error) {
      if (error.code !== 'MODULE_NOT_FOUND') {
        throw error
      }
    }
  }

  throw new Error(`[import|Legacy] Component ${componentName} not found`)
}
